import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import BrandHeader from "@/components/BrandHeader";

function formatError(detail) {
  if (!detail) return "Login failed.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Col.");
      nav(loc.state?.from || "/admin", { replace: true });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <BrandHeader minimal />
      <main className="max-w-md mx-auto px-6 sm:px-8 py-16 sm:py-24">
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-primary)] font-semibold mb-4">
          Admin sign-in
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">
          Welcome back.
        </h1>
        <p className="mt-3 text-[var(--tt-muted)]">Sign in to review candidate submissions.</p>

        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--tt-muted)]">Email</span>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="mt-2 w-full rounded-xl border border-[var(--tt-border)] bg-[var(--tt-surface)] px-4 py-3 focus:outline-none focus:border-[var(--tt-primary)] focus:ring-2 focus:ring-[var(--tt-primary)]/20"
              placeholder="you@traveltrucks.com.au"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--tt-muted)]">Password</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="mt-2 w-full rounded-xl border border-[var(--tt-border)] bg-[var(--tt-surface)] px-4 py-3 focus:outline-none focus:border-[var(--tt-primary)] focus:ring-2 focus:ring-[var(--tt-primary)]/20"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center px-6 py-4 rounded-xl bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-hover)] text-white font-medium tracking-wide transition-colors disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <Link
          to="/"
          data-testid="login-back-home"
          className="mt-8 inline-block text-xs uppercase tracking-[0.2em] text-[var(--tt-muted)] hover:text-[var(--tt-primary)]"
        >
          ← Back to home
        </Link>
      </main>
    </div>
  );
}
